import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Sparkles, ArrowRight, Loader2, Copy, Twitter, Linkedin,
  Facebook, MessageCircle, TrendingUp, Calendar, FileText,
  ChevronRight, Zap, Check
} from "lucide-react";
import { API, LOGO_URL } from "@/config";

const DAY_COLORS = {
  "Monday": "bg-violet-500/20 text-violet-400 border-violet-500/30",
  "Tuesday": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "Wednesday": "bg-teal-500/20 text-teal-400 border-teal-500/30",
  "Thursday": "bg-amber-500/20 text-amber-400 border-amber-500/30",
  "Friday": "bg-purple-500/20 text-purple-400 border-purple-500/30",
  "Saturday": "bg-pink-500/20 text-pink-400 border-pink-500/30",
  "Sunday": "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
};

const TYPE_BADGES = {
  "Blog Post": "bg-violet-600/20 text-violet-400",
  "Twitter Thread": "bg-blue-400/20 text-blue-300",
  "LinkedIn Post": "bg-blue-600/20 text-blue-400",
  "Social Media": "bg-teal-500/20 text-teal-400",
};

export default function NicheLandingPage() {
  const { nicheSlug } = useParams();
  const navigate = useNavigate();
  const [niche, setNiche] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/niches/${nicheSlug}`).then(res => {
      setNiche(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [nicheSlug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
    </div>
  );

  if (!niche) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-4">Niche Not Found</h1>
        <Link to="/solutions"><Button className="rounded-full bg-violet-600 hover:bg-violet-700">Browse All Niches</Button></Link>
      </div>
    </div>
  );

  const pageUrl = `${window.location.origin}/solutions/${nicheSlug}`;

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(niche.share_caption)}&url=${encodeURIComponent(pageUrl)}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(pageUrl)}&title=${encodeURIComponent(niche.meta_title)}&summary=${encodeURIComponent(niche.share_caption)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}&quote=${encodeURIComponent(niche.share_caption)}`,
    whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(niche.share_caption + " " + pageUrl)}`,
  };

  return (
    <>
      <Helmet>
        <title>{niche.meta_title}</title>
        <meta name="description" content={niche.meta_description} />
        <meta property="og:title" content={niche.meta_title} />
        <meta property="og:description" content={niche.meta_description} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Creatara" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={niche.meta_title} />
        <meta name="twitter:description" content={niche.meta_description} />
        <meta name="keywords" content={`${niche.name} content, ${niche.name} monetization, AI content for ${niche.name.toLowerCase()}, content creator tools, Creatara`} />
        <link rel="canonical" href={pageUrl} />
      </Helmet>

      <div className="min-h-screen bg-background">
        <nav className="fixed top-0 w-full z-50 glass">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt={`Creatara - AI Content for ${niche.name}`} className="w-10 h-10 rounded-lg" />
              <span className="text-xl font-bold">Creatara</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/solutions" className="text-muted-foreground hover:text-foreground text-sm hidden sm:block">All Solutions</Link>
              <Button onClick={() => navigate("/register")} className="btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full px-6" data-testid="niche-cta-top">
                Start Free <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="hero-glow pt-28 pb-16 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Badge className="mb-6 bg-violet-600/10 text-violet-500 border-violet-600/20 px-4 py-1">
                Built for {niche.name}
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight gradient-text mb-4" data-testid="niche-title">
                AI Content for {niche.name}
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                {niche.description}
              </p>
              <Button onClick={() => navigate("/register")} size="lg" className="btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full px-8 h-14 text-base" data-testid="niche-cta-hero">
                <Sparkles className="w-5 h-5 mr-2" /> Start for Free
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Sample Content Calendar */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <div className="text-center mb-10">
                <Badge className="mb-4 bg-teal-600/10 text-teal-500 border-teal-600/20 px-4 py-1">
                  <Calendar className="w-3 h-3 mr-1 inline" /> Sample Calendar
                </Badge>
                <h2 className="text-3xl sm:text-4xl font-bold mb-2">Your 7-Day Content Calendar</h2>
                <p className="text-muted-foreground">Here's what a week of AI-generated content looks like for {niche.name.toLowerCase()}</p>
              </div>

              <div className="space-y-3">
                {niche.calendar.map((day, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 + i * 0.07 }}
                  >
                    <Card className="glass card-hover">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className={`w-20 shrink-0 text-center py-2 rounded-lg border ${DAY_COLORS[day.day]}`}>
                          <p className="text-xs font-medium">{day.day}</p>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{day.topic}</p>
                        </div>
                        <Badge className={`shrink-0 text-xs ${TYPE_BADGES[day.content_type] || "bg-white/10"}`}>
                          {day.content_type}
                        </Badge>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              <div className="text-center mt-8">
                <p className="text-muted-foreground text-sm mb-4">This is just a sample. Creatara generates unlimited content for your niche.</p>
                <Button onClick={() => navigate("/register")} className="btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full px-8" data-testid="niche-cta-calendar">
                  <Sparkles className="w-4 h-4 mr-2" /> Generate Your Calendar Free
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 px-6 bg-card/30">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8">Why {niche.name} Choose Creatara</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: Sparkles, title: "AI Content Generation", desc: `Generate ${niche.name.toLowerCase()}-specific blog posts, social media, and ad copy in seconds` },
                { icon: TrendingUp, title: "Revenue Optimization", desc: "Every piece of content is optimized for engagement, leads, and conversions" },
                { icon: Zap, title: "Multi-Platform Sharing", desc: "Share to X/Twitter, LinkedIn, Facebook, and WhatsApp with one click" },
              ].map((f, i) => (
                <Card key={i} className="glass">
                  <CardContent className="p-5">
                    <f.icon className="w-8 h-8 text-violet-500 mb-3" />
                    <h3 className="font-semibold mb-1">{f.title}</h3>
                    <p className="text-sm text-muted-foreground">{f.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Share */}
        <section className="py-12 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm text-muted-foreground mb-3">Share this with other {niche.name.toLowerCase()}</p>
            <div className="flex flex-wrap gap-2 justify-center">
              <Button onClick={() => { navigator.clipboard.writeText(`${niche.share_caption} ${pageUrl}`); toast.success("Copied!"); }} variant="outline" size="sm" className="rounded-full"><Copy className="w-3 h-3 mr-1" />Copy</Button>
              <Button onClick={() => window.open(shareLinks.twitter, '_blank')} size="sm" className="rounded-full bg-black hover:bg-gray-900"><Twitter className="w-3 h-3 mr-1" />X</Button>
              <Button onClick={() => window.open(shareLinks.linkedin, '_blank')} size="sm" className="rounded-full bg-blue-700 hover:bg-blue-800"><Linkedin className="w-3 h-3 mr-1" />LinkedIn</Button>
              <Button onClick={() => window.open(shareLinks.facebook, '_blank')} size="sm" className="rounded-full bg-blue-600 hover:bg-blue-700"><Facebook className="w-3 h-3 mr-1" />Facebook</Button>
              <Button onClick={() => window.open(shareLinks.whatsapp, '_blank')} size="sm" className="rounded-full bg-green-600 hover:bg-green-700"><MessageCircle className="w-3 h-3 mr-1" />WhatsApp</Button>
            </div>
          </div>
        </section>

        {/* Related Niches */}
        {niche.related?.length > 0 && (
          <section className="py-12 px-6 bg-card/30">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-bold mb-6 text-center">Related Solutions</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {niche.related.map((r) => (
                  <Link key={r.slug} to={`/solutions/${r.slug}`}>
                    <Card className="glass card-hover h-full">
                      <CardContent className="p-4 text-center">
                        <p className="font-medium text-sm">{r.name}</p>
                        <p className="text-xs text-violet-500 mt-1 flex items-center justify-center gap-1">View <ChevronRight className="w-3 h-3" /></p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Final CTA */}
        <section className="py-20 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Grow as a {niche.name.split(" ").slice(-1)[0]}?</h2>
            <p className="text-lg text-muted-foreground mb-8">Join {niche.name.toLowerCase()} using Creatara to create revenue-driving content.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate("/register")} size="lg" className="btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full px-8 py-6 text-lg" data-testid="niche-cta-bottom">
                Start Free — No Credit Card <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </section>

        <footer className="border-t border-border py-8 px-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3"><img src={LOGO_URL} alt="Creatara - Content Monetization Platform" className="w-8 h-8 rounded-lg" /><span className="font-semibold">Creatara</span></div>
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <Link to="/solutions" className="text-muted-foreground hover:text-foreground">All Solutions</Link>
              <Link to="/templates" className="text-muted-foreground hover:text-foreground">Templates</Link>
              <Link to="/privacy" className="text-muted-foreground hover:text-foreground">Privacy</Link>
              <Link to="/terms" className="text-muted-foreground hover:text-foreground">Terms</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
